// TeacherRankWidget Component
// Displays teacher's current leaderboard rank

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeacherRank } from '../../leaderboardService';
import { supabase } from '../../supabaseClient';
import LoadingSpinner from '../LoadingSpinner';

// TODO: Set to true when ready to show leaderboard ranking
const SHOW_COMING_SOON = true;

export default function TeacherRankWidget({ isArabic }) {
  // All hooks must be called at the top level, before any conditional returns
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [rankData, setRankData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Resize handler effect - always called
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch rank effect - only runs when not showing coming soon
  useEffect(() => {
    if (SHOW_COMING_SOON) {
      setLoading(false);
      return;
    }
    
    async function fetchRank() {
      // Fetch current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await getTeacherRank(user.id, 'monthly');
      if (data && !error) {
        setRankData(data);
      }
      setLoading(false);
    }

    fetchRank();
  }, []);

  // COMING SOON VERSION
  if (SHOW_COMING_SOON) {
    return (
      <div style={{
        background: '#1E293B',
        borderRadius: isMobile ? 16 : 20,
        padding: isMobile ? 16 : 24,
        boxShadow: '0 8px 32px rgba(30, 41, 59, 0.3)',
        minHeight: isMobile ? 140 : 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background */}
        <div style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'rgba(255, 116, 38, 0.1)',
          zIndex: 0
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Icon */}
          <div style={{
            fontSize: isMobile ? 48 : 56,
            marginBottom: isMobile ? 12 : 16,
            animation: 'pulse 2s ease-in-out infinite'
          }}>
            🏆
          </div>

          {/* Title */}
          <div style={{
            fontSize: isMobile ? 14 : 16,
            color: 'rgba(255,255,255,0.9)',
            fontWeight: 700,
            marginBottom: isMobile ? 8 : 12
          }}>
            {isArabic ? 'ترتيبك الشهري' : 'Your Monthly Ranking'}
          </div>

          {/* Coming Soon Text */}
          <div style={{
            fontSize: isMobile ? 20 : 24,
            fontWeight: 800,
            color: '#FFD700',
            marginBottom: isMobile ? 6 : 8
          }}>
            {isArabic ? 'قريباً' : 'Coming Soon'}
          </div>

          {/* Description */}
          <div style={{
            fontSize: isMobile ? 11 : 13,
            color: 'rgba(255,255,255,0.7)',
            maxWidth: 250,
            lineHeight: 1.4
          }}>
            {isArabic
              ? 'سنطلق قريباً نظام التصنيف مع جوائز مميزة'
              : 'Launching soon with amazing prizes'}
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.8;
            }
          }
        `}</style>
      </div>
    );
  }

  // ORIGINAL IMPLEMENTATION (will be activated when SHOW_COMING_SOON = false)
  // Get trophy emoji based on rank
  const getTrophyEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    if (rank <= 10) return '🏅';
    return '📊';
  };

  // Get badge color based on rank
  const getBadgeColor = (rank) => {
    if (rank <= 3) return '#FFD700'; // Gold
    if (rank <= 10) return '#4CAF50'; // Green
    return '#757575'; // Gray
  };

  if (loading) {
    return (
      <div style={{
        background: '#1E293B',
        borderRadius: isMobile ? 16 : 20,
        padding: isMobile ? 16 : 24,
        boxShadow: '0 8px 32px rgba(30, 41, 59, 0.3)',
        minHeight: isMobile ? 140 : 180,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <LoadingSpinner size={60} />
        <div style={{ color: '#fff', opacity: 0.7 }}>
          {isArabic ? 'جاري التحميل...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (!rankData) {
    return (
      <div style={{
        background: '#1E293B',
        borderRadius: isMobile ? 16 : 20,
        padding: isMobile ? 16 : 24,
        boxShadow: '0 8px 32px rgba(30, 41, 59, 0.3)',
        minHeight: isMobile ? 140 : 180,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
        <div style={{ color: '#fff', fontSize: 16, marginBottom: 4 }}>
          {isArabic ? 'لا توجد بيانات' : 'No Ranking Yet'}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
          {isArabic ? 'أكمل جلسات للظهور في التصنيف' : 'Complete sessions to appear on leaderboard'}
        </div>
      </div>
    );
  }

  const { rank, total_teachers, teacher, badge } = rankData;

  return (
    <div
      style={{
        background: '#1E293B',
        borderRadius: isMobile ? 16 : 20,
        padding: isMobile ? 16 : 24,
        boxShadow: '0 8px 32px rgba(30, 41, 59, 0.3)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        cursor: 'pointer',
        minHeight: isMobile ? 140 : 200
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(30, 41, 59, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 32px rgba(30, 41, 59, 0.3)';
      }}
      onClick={() => navigate('/leaderboard')}
    >
      {/* Header */}
      <div style={{
        fontSize: isMobile ? 12 : 14,
        color: 'rgba(255,255,255,0.9)',
        fontWeight: 600,
        marginBottom: isMobile ? 8 : 12
      }}>
        {isArabic ? 'ترتيبك الشهري' : 'Your Monthly Ranking'}
      </div>

      {/* Rank Display */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? 12 : 16 }}>
        <div style={{ fontSize: isMobile ? 40 : 56, marginRight: isMobile ? 12 : 16 }}>
          {getTrophyEmoji(rank)}
        </div>
        <div>
          <div style={{
            fontSize: isMobile ? 28 : 40,
            fontWeight: 800,
            color: getBadgeColor(rank),
            lineHeight: 1
          }}>
            #{rank}
          </div>
          <div style={{
            fontSize: isMobile ? 11 : 13,
            color: 'rgba(255,255,255,0.7)',
            marginTop: isMobile ? 2 : 4
          }}>
            {isArabic ? `من أصل ${total_teachers}` : `out of ${total_teachers}`}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'flex',
        gap: isMobile ? 8 : 16,
        marginBottom: isMobile ? 8 : 12
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: isMobile ? 10 : 12, color: 'rgba(255,255,255,0.7)' }}>
            {isArabic ? 'الجلسات' : 'Sessions'}
          </div>
          <div style={{ fontSize: isMobile ? 16 : 20, color: '#fff', fontWeight: 700 }}>
            {teacher?.total_sessions || 0}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: isMobile ? 10 : 12, color: 'rgba(255,255,255,0.7)' }}>
            {isArabic ? 'التقييم' : 'Rating'}
          </div>
          <div style={{ fontSize: isMobile ? 16 : 20, color: '#fff', fontWeight: 700 }}>
            ⭐ {teacher?.avg_rating?.toFixed(1) || '0.0'}
          </div>
        </div>
      </div>

      {/* Badge Indicator */}
      {badge && (
        <div style={{
          display: 'inline-block',
          background: getBadgeColor(rank),
          color: rank <= 3 ? '#000' : '#fff',
          padding: isMobile ? '3px 10px' : '4px 12px',
          borderRadius: 12,
          fontSize: isMobile ? 10 : 12,
          fontWeight: 700,
          marginBottom: isMobile ? 8 : 12
        }}>
          {rank <= 3 ? (isArabic ? '💰 جائزة مالية' : '💰 Money Reward') : (isArabic ? '🏅 شارة' : '🏅 Badge')}
        </div>
      )}

      {/* View Leaderboard Link */}
      <div style={{
        color: '#FF7426',
        fontSize: isMobile ? 12 : 14,
        fontWeight: 600,
        marginTop: 'auto'
      }}>
        {isArabic ? 'عرض لوحة المتصدرين' : 'View Leaderboard'} →
      </div>
    </div>
  );
}
